// Custom hook for TeamView filter management with persistence and auto-selection
// CREATED: Encapsulates complex filter logic for TeamView component
// UPDATED: Implemented 7-day persistence with empty first visit and distinction between restoration vs manual changes
// UPDATED: Removed desktop/mobile distinction - unified UX across all devices
// UPDATED: Fixed preference restoration timing - now waits for both sites AND services to load before validation
// UPDATED: Added auto/manual selection mode toggle with intelligent mode switching
// UPDATED: Manual mode is now preserved when changing services - respects user's preference
// UPDATED: Profile type now imported from useTeamViewData (single source of truth)
// Features:
// - Auto-select operators based on service selection (only operators with tasks)
// - Manual selection mode: user explicitly chooses operators, bypasses auto-selection
// - Intelligent mode switching: auto when changing site, manual when changing operators
// - Manual mode preserved when changing services - allows consistent manual workflow
// - Persist user preferences (including selection mode) to localStorage with 7-day TTL
// - Restore preferences on mount without triggering auto-selection
// - First site auto-selected on first visit, services/operators require manual selection
// - Distinguish between preference restoration and manual changes
// - Consistent filter behavior on mobile and desktop
// - Wait for complete data loading before validating saved preferences

import { useState, useEffect, useCallback, useRef } from 'react';
import { Service } from '../lib/services';
import { AssignedTaskWithDetails, SiteInfo } from '../lib/execution';
import {
  saveTeamViewPreferences,
  getTeamViewPreferences,
  validatePreferences,
  TeamViewPreferences
} from '../lib/teamViewPreferences';
import type { Profile } from './useTeamViewData';

interface UseTeamViewFiltersParams {
  userId: string | null;
  sites: SiteInfo[];
  services: Service[];
  allProfilesForSite: Profile[];
  allTasksForSite: AssignedTaskWithDetails[];
  onScrollTrigger: () => void;
  viewMode?: 'calendar' | 'list';
  listPeriodType?: 'week' | 'month';
}

interface UseTeamViewFiltersReturn {
  selectedSiteId: string | null;
  selectedServiceIds: string[];
  selectedProfileIds: string[];
  selectionMode: 'auto' | 'manual';
  setSelectedSiteId: (siteId: string | null) => void;
  setSelectedServiceIds: (serviceIds: string[]) => void;
  setSelectedProfileIds: (profileIds: string[]) => void;
  setSelectionMode: (mode: 'auto' | 'manual') => void;
  handleSiteChange: (siteId: string) => void;
  handleServiceChange: (serviceIds: string[]) => void;
  handleOperatorSelectionChange: (profileIds: string[]) => void;
  resetToDefaults: () => void;
}

export function useTeamViewFilters({
  userId,
  sites,
  services,
  allProfilesForSite,
  allTasksForSite,
  onScrollTrigger,
  viewMode = 'calendar',
  listPeriodType = 'week',
}: UseTeamViewFiltersParams): UseTeamViewFiltersReturn {
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState<'auto' | 'manual'>('auto');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRestoringPreferences, setIsRestoringPreferences] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced save preferences
  const debouncedSavePreferences = useCallback(() => {
    if (!userId) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const preferences: TeamViewPreferences = {
        lastSiteId: selectedSiteId,
        lastServiceIds: selectedServiceIds,
        lastProfileIds: selectedProfileIds,
        lastDate: null,
        selectionMode: selectionMode,
        viewMode: viewMode,
        listPeriodType: listPeriodType,
      };
      saveTeamViewPreferences(userId, preferences);
    }, 500);
  }, [userId, selectedSiteId, selectedServiceIds, selectedProfileIds, selectionMode, viewMode, listPeriodType]);

  // Auto-select operators based on service selection
  const updateOperatorSelectionByTasks = useCallback((serviceIds: string[]) => {
    if (serviceIds.length === 0) {
      // No services selected: clear operator selection
      setSelectedProfileIds([]);
      return;
    }

    if (serviceIds.length === services.length && services.length > 0) {
      // All services selected: select all operators from the site
      const allOperatorIds = allProfilesForSite.map(p => p.id);
      setSelectedProfileIds(allOperatorIds);
      return;
    }

    // Partial service selection: select operators who have tasks for the selected services
    const operatorsWithTasksForServices = new Set<string>();

    allTasksForSite.forEach(task => {
      // Check if task's service is in the selected services
      if (task.template_service_id && serviceIds.includes(task.template_service_id)) {
        operatorsWithTasksForServices.add(task.assigned_to_user_id);
      }
    });

    // Convert Set to Array and auto-select these operators
    const operatorIds = Array.from(operatorsWithTasksForServices);

    // Only select operators who have tasks for the selected services
    if (operatorIds.length > 0) {
      setSelectedProfileIds(operatorIds);
    } else {
      // No operators have tasks for these services: clear selection
      setSelectedProfileIds([]);
    }
  }, [services.length, allProfilesForSite, allTasksForSite]);

  // Initialize filters on mount - restore preferences or leave empty on first visit
  useEffect(() => {
    // Wait for both sites AND services to be loaded before initializing
    if (!userId || sites.length === 0 || services.length === 0 || isInitialized) return;

    // Try to restore saved preferences
    const savedPreferences = getTeamViewPreferences(userId);

    if (savedPreferences) {
      // Preferences found: this is NOT a first visit
      setIsFirstVisit(false);
      setIsRestoringPreferences(true);

      // Validate preferences against current available data
      const validated = validatePreferences(
        savedPreferences,
        sites.map(s => s.id),
        services.map(s => s.id),
        allProfilesForSite.map(p => p.id)
      );

      // Restore validated preferences without triggering auto-selection
      if (validated.lastSiteId) {
        setSelectedSiteId(validated.lastSiteId);
      }

      if (validated.lastServiceIds.length > 0) {
        setSelectedServiceIds(validated.lastServiceIds);
      }

      if (validated.lastProfileIds.length > 0) {
        setSelectedProfileIds(validated.lastProfileIds);
      }

      // Restore selection mode
      setSelectionMode(validated.selectionMode);

      // Mark restoration as complete after a brief delay to ensure all state updates are done
      setTimeout(() => {
        setIsRestoringPreferences(false);
      }, 100);
    } else {
      // No saved preferences: First visit - select first site by default
      // User must manually select services and operators
      setIsFirstVisit(true);
      if (sites.length > 0) {
        setSelectedSiteId(sites[0].id);
      }
      // Services and operators remain empty - user must select manually
    }

    setIsInitialized(true);
  }, [userId, sites, services, allProfilesForSite, isInitialized]);

  // Auto-select operators when tasks load or services change (only in auto mode and not restoring)
  useEffect(() => {
    if (!isInitialized || isRestoringPreferences || selectionMode !== 'auto') return;

    // Only trigger auto-selection if we have tasks and services selected
    // and we're in auto mode and NOT restoring preferences
    if (allTasksForSite.length > 0 && selectedServiceIds.length > 0) {
      updateOperatorSelectionByTasks(selectedServiceIds);
    }
  }, [allTasksForSite, selectedServiceIds, isInitialized, isRestoringPreferences, selectionMode, updateOperatorSelectionByTasks]);

  // Save preferences when filters change (debounced)
  useEffect(() => {
    if (isInitialized) {
      debouncedSavePreferences();
    }
  }, [selectedSiteId, selectedServiceIds, selectedProfileIds, isInitialized, debouncedSavePreferences]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleSiteChange = useCallback((siteId: string) => {
    setSelectedSiteId(siteId);
    // Mark as no longer first visit when user makes a selection
    setIsFirstVisit(false);
    // Clear service selection - user must choose
    setSelectedServiceIds([]);
    setSelectedProfileIds([]);
    // Reset to auto mode when changing site
    setSelectionMode('auto');
    onScrollTrigger();
  }, [onScrollTrigger]);

  const handleServiceChange = useCallback((serviceIds: string[]) => {
    setSelectedServiceIds(serviceIds);
    // Only update operator selection automatically if in auto mode
    // In manual mode, preserve the user's explicit operator selection
    if (selectionMode === 'auto') {
      updateOperatorSelectionByTasks(serviceIds);
    }
    onScrollTrigger();
  }, [selectionMode, updateOperatorSelectionByTasks, onScrollTrigger]);

  const handleOperatorSelectionChange = useCallback((profileIds: string[]) => {
    setSelectedProfileIds(profileIds);
    // Switch to manual mode when user manually changes operator selection
    setSelectionMode('manual');
    onScrollTrigger();
  }, [onScrollTrigger]);

  const resetToDefaults = useCallback(() => {
    if (sites.length > 0) {
      setSelectedSiteId(sites[0].id);
    }
    if (services.length > 0) {
      setSelectedServiceIds(services.map(s => s.id));
    }
    setSelectedProfileIds([]);
  }, [sites, services]);

  return {
    selectedSiteId,
    selectedServiceIds,
    selectedProfileIds,
    selectionMode,
    setSelectedSiteId,
    setSelectedServiceIds,
    setSelectedProfileIds,
    setSelectionMode,
    handleSiteChange,
    handleServiceChange,
    handleOperatorSelectionChange,
    resetToDefaults
  };
}
